/**
 * IconButton color variants + sizes. Names mirror Figma `comp/button` variant
 * values 1:1 (same grammar as {@link Button}) so design ↔ code mapping stays
 * mechanical — `IconButton` reuses the button component tokens rather than
 * inventing its own, since it's the icon-only sibling of the same control.
 */
export type IconButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "destructive" | "link";

/** Container size — `sm` = 32px, `md` = 36px, `lg` = 40px. Matches Figma's `size` variant. */
export type IconButtonSize = "sm" | "md" | "lg";

interface IconButtonVariantTokens {
  /**
   * Tailwind background + text-color classes for the resting state (plus
   * `border` for `outline`, which has no fill of its own).
   */
  base: string;
  /**
   * `before:bg-comp-button-<variant>-state-hover` — painted on the `before:`
   * overlay layer (see {@link IconButton}), not directly on the button
   * background, so it composes with an already-opaque `base` fill.
   */
  stateHover: string;
  /** `before:bg-comp-button-<variant>-state-focus`, `focus-visible:`-scoped. */
  stateFocus: string;
  /** `before:bg-comp-button-<variant>-state-pressed`, `active:`-scoped. */
  statePressed: string;
}

/**
 * Variant → token mapping. Consumed internally by {@link IconButton}.
 *
 * Source of truth for the underlying CSS variables is
 * `packages/ui/base/variables.css` (`--comp-button-*`) — `tailwind-theme.css`
 * only re-exposes those same variables as `--color-comp-button-*` for
 * Tailwind's theme layer, it does not define them.
 */
export const ICON_BUTTON_VARIANTS: Record<IconButtonVariant, IconButtonVariantTokens> = {
  primary: {
    base: "bg-comp-button-primary-bg text-comp-button-primary-label",
    stateHover: "hover:before:bg-comp-button-primary-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-primary-state-focus",
    statePressed: "active:before:bg-comp-button-primary-state-pressed",
  },
  secondary: {
    base: "bg-comp-button-secondary-bg text-comp-button-secondary-label",
    stateHover: "hover:before:bg-comp-button-secondary-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-secondary-state-focus",
    statePressed: "active:before:bg-comp-button-secondary-state-pressed",
  },
  outline: {
    base: "border border-comp-button-outlined-border text-comp-button-outlined-label",
    stateHover: "hover:before:bg-comp-button-outlined-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-outlined-state-focus",
    statePressed: "active:before:bg-comp-button-outlined-state-pressed",
  },
  ghost: {
    base: "text-comp-button-ghost-label",
    stateHover: "hover:before:bg-comp-button-ghost-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-ghost-state-focus",
    statePressed: "active:before:bg-comp-button-ghost-state-pressed",
  },
  destructive: {
    base: "bg-comp-button-destructive-bg text-comp-button-destructive-label",
    stateHover: "hover:before:bg-comp-button-destructive-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-destructive-state-focus",
    statePressed: "active:before:bg-comp-button-destructive-state-pressed",
  },
  link: {
    base: "text-comp-button-link-label",
    stateHover: "hover:before:bg-comp-button-link-state-hover",
    stateFocus: "focus-visible:before:bg-comp-button-link-state-focus",
    statePressed: "active:before:bg-comp-button-link-state-pressed",
  },
};

/** Size → container class. `sm`/`md`/`lg` map to Figma's 32/36/40px containers. */
export const ICON_BUTTON_SIZES: Record<IconButtonSize, string> = {
  sm: "size-8",
  md: "size-9",
  lg: "size-10",
};
